import { Edge, MarkerType,Node } from '@xyflow/react';
import { useMemo } from 'react';

interface ModelField {
  type?: string;
  primary?: boolean;
  required?: boolean;
  unique?: boolean;
  references?: string;
}

interface Model {
  description?: string;
  fields?: Record<string, ModelField>;
}

interface ParsedYAML {
  models?: Record<string, Model>;
}

interface DiagramData {
  nodes: Node[];
  edges: Edge[];
}

export function useDiagramBuilder( parsedYAML: ParsedYAML | null ): DiagramData {
  return useMemo( () => {
    if ( !parsedYAML?.models ) {
      return { nodes: [], edges: [] };
    }

    const nodes: Node[] = [];
    const edges: Edge[] = [];
    const modelNames = Object.keys( parsedYAML.models );

    // Create nodes for each model
    modelNames.forEach( ( modelName, index ) => {
      const model = parsedYAML.models![modelName];

      // Position nodes in a grid layout
      const nodesPerRow = 3;
      const row = Math.floor( index / nodesPerRow );
      const col = index % nodesPerRow;
      const xSpacing = 350;
      const ySpacing = 300;

      nodes.push( {
        id: modelName,
        type: 'entity',
        position: { x: col * xSpacing, y: row * ySpacing },
        data: {
          label: modelName,
          fields: model.fields || {}
        }
      } );

      // Create edges based on foreign key relationships
      if ( model.fields ) {
        Object.entries( model.fields ).forEach( ( [ fieldName, field ] ) => {
          // Check if field references another model (foreign key)
          if ( field.references ) {
            const targetModel = field.references;
            if ( modelNames.includes( targetModel ) ) {
              edges.push( {
                id: `${ modelName }-${ fieldName }-${ targetModel }`,
                source: modelName,
                target: targetModel,
                label: fieldName,
                type: 'smoothstep',
                animated: false,
                markerEnd: { type: MarkerType.ArrowClosed }
              } );
            }
          }
        } );
      }
    } );

    return { nodes, edges };
  }, [ parsedYAML ] );
}
